"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ItemBox = void 0;
var _components = require("@ori-ui/components");
var _icons = require("@ori-ui/icons");
var _mui = require("@ori-ui/mui");
var _culture = require("@ori/culture");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _react = require("react");
var _constants = require("../../../constants");
var _contexts = require("../../../contexts");
var _utils = require("../../../utils");
var _FormattedMessage = require("../../FormattedMessage");
var _shared = require("../../shared");
var _Image = require("../Image");
var _ItemBoxDescription = require("./ItemBoxDescription");
var _SelectedIcon = require("./SelectedIcon");
var _jsxRuntime = require("react/jsx-runtime");
/* eslint-disable @typescript-eslint/naming-convention -- using data-testid property */

const Root = (0, _mui.styled)(_mui.Box)(({
  ownerState: {
    isBadgeDisplayed,
    isDisabled,
    isDoubleCurrency,
    isOutOfStock,
    isRequirementMet,
    isSelected,
    wasAvailabilityChange
  },
  theme
}) => ({
  background: isOutOfStock ? (0, _mui.alpha)(theme.palette.error.light, 0.04) : theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[200]}`,
  ...(isSelected && {
    border: `1px solid ${theme.palette.grey.A400}`
  }),
  ...(isSelected && wasAvailabilityChange && {
    border: `1px solid ${theme.palette.error.main}`
  }),
  borderRadius: theme.spacing(2),
  boxShadow: `0 0 8px ${theme.palette.grey.A100}`,
  boxSizing: 'border-box',
  cursor: isSelected || isOutOfStock || isDisabled ? 'default' : 'pointer',
  height: _constants.ITEM_HEIGHT,
  marginTop: isBadgeDisplayed ? theme.spacing(2) : 0,
  position: 'relative',
  width: _constants.ITEM_WIDTH,
  ...((isDisabled || isRequirementMet) && !isSelected && {
    opacity: 0.5
  }),
  ...(isDoubleCurrency && {
    height: _constants.ITEM_HEIGHT + _constants.DOUBLE_CURRENCY_HEIGHT_EXTENSION
  }),
  [theme.breakpoints.only('xs')]: {
    height: _constants.ITEM_HEIGHT_XS,
    width: _constants.ITEM_WIDTH_XS,
    ...(isDoubleCurrency && {
      height: _constants.ITEM_HEIGHT_XS + _constants.DOUBLE_CURRENCY_HEIGHT_EXTENSION
    })
  },
  [theme.breakpoints.down(_constants.XXS)]: {
    height: _constants.ITEM_HEIGHT_XXS,
    width: _constants.ITEM_WIDTH_XXS,
    ...(isDoubleCurrency && {
      height: _constants.ITEM_HEIGHT_XXS + _constants.DOUBLE_CURRENCY_HEIGHT_EXTENSION
    })
  }
}));
const DiscountTagIcon = (0, _mui.styled)(_icons.DiscountTagFilled)(({
  theme
}) => ({
  fontSize: '2.6rem'
}));
const ItemImageWrapper = (0, _mui.styled)('div')(({
  theme
}) => ({
  borderRadius: theme.spacing(2, 2, 0, 0),
  height: _constants.ITEM_WIDTH,
  overflow: 'hidden',
  position: 'relative',
  marginLeft: 1,
  [theme.breakpoints.only('xs')]: {
    height: _constants.ITEM_WIDTH_XS
  },
  [theme.breakpoints.down(_constants.XXS)]: {
    height: _constants.ITEM_WIDTH_XXS
  }
}));
const OosLabel = (0, _mui.styled)('div')(({
  theme
}) => ({
  zIndex: 1,
  position: 'absolute',
  bottom: 0,
  overflow: 'hidden',
  width: '100%',
  height: '24px',
  lineHeight: '24px',
  textAlign: 'center',
  fontSize: '1.1rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: theme.palette.error.main,
  background: (0, _mui.alpha)(theme.palette.error.light, 0.15)
}));
const PickerHolder = (0, _mui.styled)('div')(({
  theme
}) => ({
  display: 'flex',
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 45,
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(0, 3),
  background: 'transparent'
}));
const ReplacementLabel = (0, _mui.styled)(_mui.Typography)(({
  ownerState: {
    isSelected
  },
  theme
}) => ({
  bottom: 0,
  color: theme.palette.text.disabled,
  fontSize: '1rem',
  lineHeight: '1.3rem',
  overflow: 'hidden',
  paddingBottom: theme.spacing(1),
  position: 'absolute',
  textAlign: 'center',
  textTransform: 'uppercase',
  width: '100%',
  ...(isSelected && {
    filter: 'blur(2px)'
  })
}));
const ItemBox = ({
  'data-testid': dataTestId,
  ...props
}) => {
  const {
    isDoubleCurrency
  } = (0, _contexts.useCheckoutOfferHints)();
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const {
    availableQuantity,
    couldDeselect,
    formattedPrecentDiscount,
    imageUrl,
    isBadgeDisplayed,
    isDisabled,
    isOutOfStock,
    isRequirementMet,
    isSelected,
    name,
    onChangeQuantity,
    onToggleItem,
    price: rawPrice,
    purchasedQuantity,
    purchasePrice: rawPurchasePrice,
    replacement,
    selectedQuantity,
    wasAvailabilityChange = false
  } = props;
  const price = (0, _culture.useFormatCurrency)(rawPrice);
  const purchasePrice = (0, _culture.useFormatCurrency)(rawPurchasePrice);
  const canIncrease = !isRequirementMet && (availableQuantity === undefined || selectedQuantity < availableQuantity);
  const ownerState = {
    isBadgeDisplayed,
    isDisabled,
    isDoubleCurrency,
    isOutOfStock,
    isRequirementMet,
    isSelected,
    wasAvailabilityChange
  };
  const handleToggleItem = (0, _react.useCallback)(() => {
    if (isDisabled) {
      return;
    }
    if (!isSelected && !isOutOfStock) {
      onToggleItem(true);
    } else if (couldDeselect) {
      onToggleItem(false);
    }
  }, [isDisabled, isSelected, isOutOfStock, couldDeselect, onToggleItem]);
  const handleChangeQuantity = (0, _react.useCallback)(newQuantity => {
    if (!(0, _utils.isValidQuantity)(newQuantity)) {
      return;
    }
    onChangeQuantity(Number(newQuantity));
  }, [onChangeQuantity]);
  const handleSelectItem = (0, _react.useCallback)(() => {
    if (!isSelected) {
      handleToggleItem();
    }
  }, [isSelected, handleToggleItem]);
  const quantityPickerProps = {
    IncreaseIconButtonProps: {
      disabled: !canIncrease
    },
    ...(availableQuantity !== undefined && {
      maxValue: availableQuantity
    }),
    minValue: purchasedQuantity,
    onChange: handleChangeQuantity,
    value: selectedQuantity
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(Root, {
    ownerState: ownerState,
    "data-testid": dataTestId,
    onClick: handleSelectItem,
    children: [isBadgeDisplayed ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_shared.Badge, {
      ownerState: {
        isIcon: false
      },
      "data-testid": getTestId('badge'),
      children: formattedPrecentDiscount ?? /*#__PURE__*/(0, _jsxRuntime.jsx)(DiscountTagIcon, {})
    }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_SelectedIcon.SelectedIcon, {
      "data-testid": getTestId('selected-icon'),
      isSelected: isSelected,
      onClick: handleToggleItem
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(ItemImageWrapper, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Image.ItemImage, {
        name: name,
        src: imageUrl
      }), isSelected ? /*#__PURE__*/(0, _jsxRuntime.jsx)(PickerHolder, {
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.QuantityPicker, {
          "data-testid": getTestId('quantity-picker'),
          ...quantityPickerProps
        })
      }) : null, isOutOfStock ? /*#__PURE__*/(0, _jsxRuntime.jsx)(OosLabel, {
        "data-testid": getTestId('out-of-stock-label'),
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_FormattedMessage.FormattedMessage, {
          id: "outOfStock"
        })
      }) : null, !isOutOfStock && replacement ? /*#__PURE__*/(0, _jsxRuntime.jsx)(ReplacementLabel, {
        "data-testid": getTestId('replacement-label'),
        variant: "body1",
        color: "inherit",
        ownerState: ownerState,
        children: replacement
      }) : null]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_ItemBoxDescription.ItemBoxDescription, {
      ...props,
      "data-testid": getTestId('item-description'),
      isDoubleCurrency: isDoubleCurrency,
      price: price,
      priceColor: selectedQuantity > purchasedQuantity ? 'text.secondary' : 'grey.A400',
      purchasePrice: purchasePrice
    })]
  });
};
exports.ItemBox = ItemBox;